import { useReactiveVar } from '@apollo/client/react';
import { EditNote, FlashOn, NoteAdd, Assignment } from '@mui/icons-material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import VideoChatIcon from '@mui/icons-material/VideoChat';
import { SpeedDial, SpeedDialIcon, SpeedDialAction } from '@mui/material';
import { map } from 'lodash';
import React, { useContext, useState } from 'react';

import RequestAccessBlock from '../RequestPermissionTooltip/RequestAccessBlock';

import ControlledModals from './ControlledModals';

import PermissionContext from '@/Contexts/Permissions';
import { PermissionActionEnum, PermissionEntityEnum } from '@/graphql/generated/graphql';
import { type ModalType, setOpenedModals } from '@/graphql/reactiveVars';

interface SpeedDialActionItem {
  icon: React.ReactNode;
  name: string;
  permissible: { entity: PermissionEntityEnum; action: PermissionActionEnum } | null;
  modal: ModalType;
  hideSpiderOnClick?: boolean;
}

const speedDialActions: SpeedDialActionItem[] = [
  { icon: <EditNote />, name: 'Open Notes', permissible: null, modal: 'Note' },
  {
    icon: <Assignment />,
    name: 'Open Tasks',
    permissible: { entity: PermissionEntityEnum.Tasks, action: PermissionActionEnum.View },
    modal: 'Tasks',
  },
  { icon: <NoteAdd />, name: 'Create Note', permissible: null, modal: 'CreateNote' },
  {
    icon: <EditCalendarIcon />,
    name: 'Create Event',
    permissible: { entity: PermissionEntityEnum.Events, action: PermissionActionEnum.Create },
    modal: 'CreateEvent',
  },
  {
    icon: <CreateNewFolderIcon />,
    name: 'Create Project',
    permissible: { entity: PermissionEntityEnum.Projects, action: PermissionActionEnum.Create },
    modal: 'CreateProject',
    hideSpiderOnClick: true,
  },
  {
    icon: <AddBoxIcon />,
    name: 'Create Task',
    permissible: { entity: PermissionEntityEnum.Tasks, action: PermissionActionEnum.Create },
    modal: 'CreateTask',
  },
  {
    icon: <FlashOn />,
    name: 'Create Kick Off',
    permissible: { entity: PermissionEntityEnum.ProjectKickOff, action: PermissionActionEnum.Create },
    modal: 'KickOff',
    hideSpiderOnClick: true,
  },
  {
    icon: <VideoChatIcon />,
    name: 'New Meeting',
    permissible: { entity: PermissionEntityEnum.Projects, action: PermissionActionEnum.Create },
    modal: 'Meeting',
  },
];

const ShortcutSpider: React.FC = () => {
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const openedModal = useReactiveVar(setOpenedModals);
  const [open, setOpen] = useState(false);

  const handleSpeedDialActionClick = (action: SpeedDialActionItem) => {
    // Remove the incoming modal to prevent duplicates in the array
    let nextModals = openedModal.filter((modal) => modal !== action.modal);

    // Enforce mutual exclusivity for 'Tasks' and 'Note'
    if (action.modal === 'Tasks') {
      nextModals = nextModals.filter((modal) => modal !== 'Note');
    } else if (action.modal === 'Note') {
      nextModals = nextModals.filter((modal) => modal !== 'Tasks');
    }

    // Update the state with the cleaned array + the new modal
    setOpenedModals([...nextModals, action.modal]);
    setOpen(false);
  };

  return (
    <React.Fragment>
      <SpeedDial
        ariaLabel="overwatch actions shortcut"
        // id="action-spider"
        direction="left"
        sx={{ position: 'absolute', bottom: 16, right: 16, zIndex: 1400 }}
        icon={<SpeedDialIcon />}
        open={open}
        // only close on explicit toggle / Esc — NOT on mouseLeave or blur, which is what
        // collapsed the dial when users drifted off it
        onClose={(_event, reason) => {
          if (reason === 'toggle' || reason === 'escapeKeyDown') setOpen(false);
        }}
        onOpen={(_event, reason) => {
          if (reason === 'toggle') setOpen(true);
        }}
        // workaround for MUI controlled-SpeedDial bug (onOpen immediately followed by onClose
        // on first tap): drive the toggle from the Fab's own click instead of the root
        FabProps={{
          onClick: () => setOpen((prev) => !prev),
        }}
      >
        {map(speedDialActions, (action) => {
          // check for permission if a permissible entity is defined, it its not defined that it doesn't need a permission check
          const hasPermission = action.permissible
            ? canPerformAction(action.permissible.action, action.permissible.entity)
            : true;

          return (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={
                !hasPermission && action.permissible?.entity ? (
                  <RequestAccessBlock
                    requiredPermission={{
                      action: action.permissible.action,
                      entity: action.permissible.entity,
                    }}
                    checkRequestExists={checkRequestExists}
                    onRequest={requestPermission}
                  />
                ) : (
                  action.name
                )
              }
              onClick={() => {
                if (!hasPermission) return;
                return handleSpeedDialActionClick(action);
              }}
            />
          );
        })}
      </SpeedDial>
      <ControlledModals />
    </React.Fragment>
  );
};

export default ShortcutSpider;
